import type { BONUSX_SUBSCRIPTION_TIERS } from "../../../../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import type { IChatbotMessage } from "../../../entities/chatbot-message.entity";
import type { FeedbackEnum } from "../../../enums/feedback.enum";
import type { NegativeFeedbackReasonEnum } from "../../../enums/negative-feedback-reason.enum";

export enum AI_CHATBOT_ACTIONS {
  POPULATE_CHAT_MESSAGES = "POPULATE_CHAT_MESSAGES",
  ADD_USER_MESSAGE_ACTION = "ADD_USER_MESSAGE",
  ADD_AI_RESPONSE_ACTION = "ADD_AI_RESPONSE",
  SET_IS_SENDING_ACTION = "SET_IS_SENDING",
  SET_SHOW_EMPTY_STATE_ACTION = "SET_SHOW_EMPTY_STATE_ACTION",
  SET_SCROLL_TO_BOTTOM_ACTION = "SET_SCROLL_TO_BOTTOM_ACTION",
  SET_SHOW_SCROLL_TO_BOTTOM_BUTTON_ACTION = "SET_SHOW_SCROLL_TO_BOTTOM_BUTTON_ACTION",
  SET_IS_STREAMING_RESPONSE = "SET_IS_STREAMING_RESPONSE",
  SET_IS_CREATING_NEW_SESSION = "SET_IS_CREATING_NEW_SESSION",
  CLEAR_CHAT_MESSAGES = "CLEAR_CHAT_MESSAGES",
  SET_CAN_CLONE_FROM_PREVIOUS_CHAT = "SET_CAN_CLONE_FROM_PREVIOUS_CHAT",
  SET_IS_CLONING_FROM_PREVIOUS_CHAT = "SET_IS_CLONING_FROM_PREVIOUS_CHAT",
  SET_IS_AVAILABLE_IN_CURRENT_SUBSCRIPTION = "SET_IS_AVAILABLE_IN_CURRENT_SUBSCRIPTION",
  SET_USER_SUBSCRIPTION_TIER = "SET_USER_SUBSCRIPTION_TIER",
  ADD_AI_RESPONSE_STREAM_ACTION = "AI_RESPONSE_STREAM_ACTION",
  SET_ERROR_WHILE_GENERATING_RESPONSE = "SET_ERROR_WHILE_GENERATING_RESPONSE",
  RESET = "RESET",
  SET_REQUIRED_TIER_FOR_CHATBOT = "SET_REQUIRED_TIER_FOR_CHATBOT",
  SET_RATE_LIMIT_EXCEEDED = "SET_RATE_LIMIT_EXCEEDED",
  SET_ERROR_MESSAGE = "SET_ERROR_MESSAGE",
  SET_SHOW_RETRY_BUTTON = "SET_SHOW_RETRY_BUTTON",
  SET_RETRY_BUTTON_TEXT = "SET_RETRY_BUTTON_TEXT",
  SET_MESSAGE_FEEDBACK = "SET_MESSAGE_FEEDBACK",
  SET_MESSAGE_NEGATIVE_FEEDBACK_REASON = "SET_MESSAGE_NEGATIVE_FEEDBACK_REASON",
}

export type PopulateChatMessagesAction = {
  type: AI_CHATBOT_ACTIONS.POPULATE_CHAT_MESSAGES;
  payload: { messages: IChatbotMessage[] };
};

export type AddUserMessageAction = {
  type: AI_CHATBOT_ACTIONS.ADD_USER_MESSAGE_ACTION;
  payload: { message: IChatbotMessage };
};

export type AddAiResponseAction = {
  type: AI_CHATBOT_ACTIONS.ADD_AI_RESPONSE_ACTION;
  payload: { message: IChatbotMessage };
};

export type SetIsSendingAction = {
  type: AI_CHATBOT_ACTIONS.SET_IS_SENDING_ACTION;
  payload: { isSending: boolean };
};

export type SetShowEmptyStateAction = {
  type: AI_CHATBOT_ACTIONS.SET_SHOW_EMPTY_STATE_ACTION;
  payload: { showEmptyState: boolean };
};

export type SetScrollToBottomAction = {
  type: AI_CHATBOT_ACTIONS.SET_SCROLL_TO_BOTTOM_ACTION;
  payload: { scrollToBottom: boolean };
};

export type SetShowScrollToBottomButtonAction = {
  type: AI_CHATBOT_ACTIONS.SET_SHOW_SCROLL_TO_BOTTOM_BUTTON_ACTION;
  payload: { showScrollToBottomButton: boolean };
};

export type SetIsStreamingResponseAction = {
  type: AI_CHATBOT_ACTIONS.SET_IS_STREAMING_RESPONSE;
  payload: { isStreamingResponse: boolean };
};

export type SetIsCreatingNewSessionAction = {
  type: AI_CHATBOT_ACTIONS.SET_IS_CREATING_NEW_SESSION;
  payload: { isCreatingNewSession: boolean };
};

export type ClearChatMessagesAction = {
  type: AI_CHATBOT_ACTIONS.CLEAR_CHAT_MESSAGES;
};

export type SetCanCloneFromPreviousChatAction = {
  type: AI_CHATBOT_ACTIONS.SET_CAN_CLONE_FROM_PREVIOUS_CHAT;
  payload: { canCloneFromPreviousChat: boolean };
};

export type SetIsCloningFromPreviousChatAction = {
  type: AI_CHATBOT_ACTIONS.SET_IS_CLONING_FROM_PREVIOUS_CHAT;
  payload: { isCloningFromPreviousChat: boolean };
};

export type SetIsAvailableInCurrentSubscriptionAction = {
  type: AI_CHATBOT_ACTIONS.SET_IS_AVAILABLE_IN_CURRENT_SUBSCRIPTION;
  payload: { isAvailableInCurrentSubscription: boolean };
};

export type SetUserSubscriptionTierAction = {
  type: AI_CHATBOT_ACTIONS.SET_USER_SUBSCRIPTION_TIER;
  payload: { userSubscriptionTier: BONUSX_SUBSCRIPTION_TIERS };
};

export type AddAiResponseStreamAction = {
  type: AI_CHATBOT_ACTIONS.ADD_AI_RESPONSE_STREAM_ACTION;
  payload: { message: IChatbotMessage };
};

export type SetErrorWhileGeneratingResponseAction = {
  type: AI_CHATBOT_ACTIONS.SET_ERROR_WHILE_GENERATING_RESPONSE;
  payload: { errorWhileGeneratingResponse: boolean };
};

export type ResetAction = {
  type: AI_CHATBOT_ACTIONS.RESET;
};

export type SetRequiredTierForChatbotAction = {
  type: AI_CHATBOT_ACTIONS.SET_REQUIRED_TIER_FOR_CHATBOT;
  payload: { requiredTierForChatbot: string };
};

export type SetRateLimitExceededAction = {
  type: AI_CHATBOT_ACTIONS.SET_RATE_LIMIT_EXCEEDED;
  payload: { rateLimitExceeded: boolean };
};

export type SetErrorMessageAction = {
  type: AI_CHATBOT_ACTIONS.SET_ERROR_MESSAGE;
  payload: { errorMessage: string | undefined };
};

export type SetShowRetryButtonAction = {
  type: AI_CHATBOT_ACTIONS.SET_SHOW_RETRY_BUTTON;
  payload: { showRetryButton: boolean };
};

export type SetRetryButtonTextAction = {
  type: AI_CHATBOT_ACTIONS.SET_RETRY_BUTTON_TEXT;
  payload: { retryButtonText: string | undefined };
};

export type SetMessageFeedbackAction = {
  type: AI_CHATBOT_ACTIONS.SET_MESSAGE_FEEDBACK;
  payload: { messageId: number; feedback: FeedbackEnum | null };
};

export type SetMessageNegativeFeedbackReasonAction = {
  type: AI_CHATBOT_ACTIONS.SET_MESSAGE_NEGATIVE_FEEDBACK_REASON;
  payload: { messageId: number; reason: NegativeFeedbackReasonEnum };
};

export type ChatbotBaseActions = ResetAction;

export type ChatbotMessagesActions =
  | PopulateChatMessagesAction
  | AddUserMessageAction
  | AddAiResponseAction
  | ClearChatMessagesAction
  | AddAiResponseStreamAction
  | SetMessageFeedbackAction
  | SetMessageNegativeFeedbackReasonAction;

export type ChatbotSubscriptionActions = SetUserSubscriptionTierAction | SetRequiredTierForChatbotAction;

export type ChatbotFlagsActions =
  | SetIsSendingAction
  | SetShowEmptyStateAction
  | SetScrollToBottomAction
  | SetShowScrollToBottomButtonAction
  | SetIsStreamingResponseAction
  | SetIsCreatingNewSessionAction
  | SetCanCloneFromPreviousChatAction
  | SetIsCloningFromPreviousChatAction
  | SetIsAvailableInCurrentSubscriptionAction
  | SetErrorWhileGeneratingResponseAction
  | SetRateLimitExceededAction
  | SetErrorMessageAction
  | SetShowRetryButtonAction
  | SetRetryButtonTextAction;

export type AllChatbotActions =
  | ChatbotBaseActions
  | ChatbotMessagesActions
  | ChatbotSubscriptionActions
  | ChatbotFlagsActions;
